import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';

import { Suspense, lazy, useContext } from 'react';

import { IntegrationSystem } from '@/shared/types';
import { ARSServiceCode } from '@/shared/types/serviceCodes';

import { Faq } from '@/components/Home/FAQ';

import AuthContext from '@/contexts/Auth/AuthContext';
import { ServicesContext } from '@/contexts/Services/ServicesContext';

import { getCmsSlides } from '@/api/apiService/cmsSlides';
import { handleEmptyResponse } from '@/api/apiService/utils';
import { queryKeys } from '@/api/queryKeys';
import { CmsSlidesResponse, ServiceType } from '@/api/types';

import api from '@/api';

import { ROUTES } from '../../../router/config';

import { CmsBanner } from './ui/CmsBanner/CmsBanner';
import { HeroInfoSlider } from './ui/HeroInfoSlider/HeroInfoSlider';
import { IncomingCorrespondenceBanner } from './ui/IncomingCorrespondenceBanner/IncomingCorrespondenceBanner';
import { ServicesHomePageList } from './ui/ServicesListSection/ServicesHomePageList';
import { Statistics } from './ui/StatisticsSection/Statistics';

import './HomePage.scss';
const CMS_SLIDES_QUERY_KEY = ['cmsSlides'] as const;
const CMS_SLIDES_EMPTY_STALE_TIME_MS = 5 * 60 * 1000;
const CMS_SLIDES_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

const getCmsSlidesParts = (slides: CmsSlidesResponse['data'] | undefined) => ({
  mainSlides: slides?.filter((slide) => slide.code == null) ?? [],
  bannerSlide: slides?.find((slide) => slide.code != null) || null,
});
const News = lazy(() => import('msp/News').catch(() => ({ default: () => null })));

const HomePage: React.FC = () => {
  const { services, isLoading: isServicesLoading, getServiceByCode } = useContext(ServicesContext);
  const { isLoading: isExternalServicesLoading, data: externalServices = [] } = useQuery({
    queryKey: queryKeys.services.external(),
    queryFn: () => api.services.getExternal().then((r) => r.data),
  });
  const { data: faqData = [], error: errorFaq } = useQuery({
    queryKey: queryKeys.faq.list(),
    queryFn: () => api.getFaq().then(handleEmptyResponse),
  });
  const { data: cmsSlides, isLoading: isCmsSlidesLoading } = useQuery<CmsSlidesResponse | null>({
    queryKey: CMS_SLIDES_QUERY_KEY,
    queryFn: getCmsSlides,
    staleTime: (query) =>
      (query.state.data as CmsSlidesResponse | null | undefined)?.data?.length
        ? CMS_SLIDES_CACHE_TTL_MS
        : CMS_SLIDES_EMPTY_STALE_TIME_MS,
    gcTime: CMS_SLIDES_CACHE_TTL_MS,
    retry: false,
  });

  const { isLoggedIn, systems } = useContext(AuthContext);
  const newsAvailable = isLoggedIn && systems?.includes(IntegrationSystem.MSP);
  const { mainSlides, bannerSlide } = getCmsSlidesParts(cmsSlides?.data);
  const showSliderPlaceholder = !isCmsSlidesLoading && mainSlides.length === 0;

  return (
    <>
      <HeroInfoSlider slides={mainSlides} showPlaceholder={showSliderPlaceholder} />
      <Statistics />
      <section id="home" className="section-light homepage">
        <div className="homepage__container">
          <ServicesHomePageList
            header="Послуги"
            servicesType={ServiceType.INTERNAL}
            services={services || []}
            redirectLink={ROUTES.INTERNAL_SERVICES}
            isLoading={isServicesLoading}
          />

          <div className="item item--full-screen-mobile">
            <CmsBanner banner={bannerSlide} />
          </div>

          {getServiceByCode(ARSServiceCode.SEARCH_INCOMING_CORRESPONDENCE) && (
            <div className="item item--full-screen-mobile">
              <IncomingCorrespondenceBanner />
            </div>
          )}

          {!errorFaq && faqData?.length > 0 && (
            <div className="item item--blue item--full-screen-mobile">
              <Faq faqData={faqData} />
            </div>
          )}
          {newsAvailable && (
            <div className="item">
              <Suspense fallback={<div style={{ minHeight: '300px' }}>Завантаження новин...</div>}>
                <News />
              </Suspense>
            </div>
          )}
          <div className={classNames('item', { 'item--blue': newsAvailable })}>
            <ServicesHomePageList
              header="Інші державні послуги"
              servicesType={ServiceType.EXTERNAL}
              services={externalServices || []}
              redirectLink={ROUTES.EXTERNAL_SERVICES}
              isLoading={isExternalServicesLoading}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
